import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bot, Loader2, ExternalLink, CheckCircle2, XCircle, Clock, Sparkles, AlertTriangle, ListChecks } from "lucide-react";

const STATUS_META = {
  queued: { label: "Queued", cls: "bg-zinc-100 text-zinc-700", icon: Clock, spin: false },
  tailoring: { label: "Tailoring CV…", cls: "bg-indigo-100 text-indigo-800", icon: Sparkles, spin: false },
  applying: { label: "Preparing…", cls: "bg-indigo-100 text-indigo-800", icon: Loader2, spin: true },
  filling: { label: "Filling form…", cls: "bg-indigo-100 text-indigo-800", icon: Loader2, spin: true },
  ready_for_review: { label: "Ready — review & submit", cls: "bg-amber-100 text-amber-800", icon: AlertTriangle, spin: false },
  submitted: { label: "Submitted", cls: "bg-green-100 text-green-800", icon: CheckCircle2, spin: false },
  review_timeout: { label: "Review timed out", cls: "bg-orange-100 text-orange-800", icon: Clock, spin: false },
  cancelled: { label: "Cancelled", cls: "bg-zinc-100 text-zinc-700", icon: XCircle, spin: false },
  unsupported: { label: "Unsupported URL", cls: "bg-zinc-100 text-zinc-700", icon: XCircle, spin: false },
  failed: { label: "Failed", cls: "bg-red-100 text-red-800", icon: XCircle, spin: false },
};

const ACTIVE = ["queued", "tailoring", "applying", "filling", "ready_for_review"];

export default function AutoApply() {
  const navigate = useNavigate();
  const [urls, setUrls] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [jobs, setJobs] = useState([]);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/autoapply/jobs");
      setJobs(data);
    } catch (e) { console.error("Failed to refresh auto-apply jobs:", e); }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parsedUrls = urls.split("\n").map((u) => u.trim()).filter(Boolean);

  const start = async () => {
    if (parsedUrls.length === 0) { toast.error("Paste at least one Greenhouse job URL"); return; }
    if (parsedUrls.length > 10) { toast.error("Up to 10 URLs at a time"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/autoapply/batch", { urls: parsedUrls });
      toast.success(`Started ${data.count} application(s). CVs are being tailored…`);
      setUrls("");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Could not start auto-apply"));
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id) => {
    try { await api.post(`/autoapply/jobs/${id}/cancel`); load(); } catch (e) { console.error("Failed to cancel auto-apply job:", e); }
  };

  const activeCount = jobs.filter((j) => ACTIVE.includes(j.status)).length;

  return (
    <div className="space-y-6" data-testid="autoapply-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-2">
          <Bot className="h-7 w-7 text-accent" strokeWidth={1.5} /> Auto-Apply <Badge variant="outline" className="ml-1 text-[10px] uppercase tracking-wider">Beta · Greenhouse</Badge>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm max-w-2xl">
          Paste up to 10 <span className="font-medium">Greenhouse</span> job URLs. Jobsmith tailors your CV (Balanced mode) and fills each application in a live browser — you review and hit submit via the live link.
        </p>
      </div>

      <Card className="border-zinc-200 shadow-sm max-w-2xl">
        <CardHeader><CardTitle className="font-heading text-lg">Job URLs</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            rows={6}
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            placeholder={"https://boards.greenhouse.io/acme/jobs/123\nhttps://job-boards.greenhouse.io/acme/jobs/456"}
            data-testid="autoapply-urls-input"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{parsedUrls.length}/10 URLs</span>
            <Button onClick={start} disabled={submitting || parsedUrls.length === 0} data-testid="autoapply-start-button" className="active:scale-[0.98] transition-transform">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Bot className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Tailor & Auto-Apply</>}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Uses your Profile (name, email, phone) + a freshly tailored CV. Standard fields are auto-filled; complete any custom questions in the live view, then submit.</p>
        </CardContent>
      </Card>

      {jobs.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-xl font-bold tracking-tight">Applications</h2>
            {activeCount > 0 && <Badge className="bg-indigo-100 text-indigo-800">{activeCount} in progress</Badge>}
          </div>
          <div className="grid grid-cols-1 gap-3" data-testid="autoapply-jobs-list">
            {jobs.map((j) => {
              const meta = STATUS_META[j.status] || STATUS_META.queued;
              const Icon = meta.icon;
              return (
                <Card key={j.id} className="border-zinc-200 shadow-sm" data-testid={`autoapply-job-${j.id}`}>
                  <CardContent className="py-4 flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{j.job_title || j.url}</p>
                      <p className="text-xs text-muted-foreground truncate">{j.company || j.url}</p>
                      {j.error && <p className="text-xs text-red-600 mt-1">{j.error}</p>}
                    </div>
                    {j.alignment?.score != null && (
                      <Badge className={`${j.alignment.score >= 80 ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{j.alignment.score}% match</Badge>
                    )}
                    <Badge className={`${meta.cls} gap-1`} data-testid={`autoapply-status-${j.id}`}>
                      <Icon className={`h-3.5 w-3.5 ${meta.spin ? "animate-spin" : ""}`} strokeWidth={2} /> {meta.label}
                    </Badge>
                    {j.status === "ready_for_review" && j.live_view_url && (
                      <>
                        <a href={j.live_view_url} target="_blank" rel="noreferrer" data-testid={`autoapply-review-${j.id}`}>
                          <Button size="sm" className="active:scale-[0.98] transition-transform"><ExternalLink className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Review & Submit</Button>
                        </a>
                        <Button size="sm" variant="ghost" onClick={() => cancel(j.id)} data-testid={`autoapply-cancel-${j.id}`}>Cancel</Button>
                      </>
                    )}
                    {j.status === "submitted" && (
                      <Button size="sm" variant="outline" onClick={() => navigate("/tracker")}><ListChecks className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Tracker</Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
